import type { RunState } from "../types/api";
import { formatClockMs, shortRunId } from "../utils/format";
import { ActivityLog } from "./ActivityLog";
import { LiveStatusFeed } from "./LiveStatusFeed";
import { PipelineRail } from "./PipelineRail";
import { SpecialistPanels } from "./SpecialistPanels";
import { StructuredQuery } from "./StructuredQuery";

interface LiveRunViewProps {
  state: RunState;
  clockMs: number;
  onRetry: () => void;
  onEditQuery: () => void;
}

export function LiveRunView({ state, clockMs, onRetry, onEditQuery }: LiveRunViewProps) {
  const showError = state.runStatus === "error";
  const cancelled = state.runStatus === "cancelled";
  const synthesizePhase = state.phaseStatus[4];
  const showSynthesis =
    synthesizePhase === "active" ||
    synthesizePhase === "done" ||
    state.supervisorAnswer.length > 0;

  return (
    <main className="body-main live-screen">
      <div className="context-strip">
        <div className="context-left">
          <p className="context-query" title={state.prompt}>
            {state.prompt}
          </p>
          {state.runId ? (
            <p className="context-run-id">run {shortRunId(state.runId)}</p>
          ) : null}
        </div>
        <div className="context-right">
          {state.framework ? (
            <span className="framework-badge">{state.framework.toUpperCase()}</span>
          ) : null}
          <span className={`run-status run-status-${state.runStatus}`}>
            <span className="status-dot" />
            {state.runStatus === "connecting"
              ? "connecting"
              : state.runStatus === "running"
                ? "running"
                : state.runStatus === "complete"
                  ? "complete"
                  : state.runStatus === "cancelled"
                    ? "cancelled"
                    : state.runStatus === "error"
                      ? "error"
                      : "idle"}
          </span>
          <span className="run-clock">{formatClockMs(clockMs)}</span>
        </div>
      </div>

      <PipelineRail phaseStatus={state.phaseStatus} phaseElapsedMs={state.phaseElapsedMs} />

      <LiveStatusFeed state={state} />

      {cancelled ? (
        <div className="notice-card">
          Run cancelled.{" "}
          <button type="button" className="link-btn" onClick={onEditQuery}>
            Run again
          </button>
        </div>
      ) : null}

      {showError ? (
        <div className="error-card">
          <p>{state.errorMessage}</p>
          <div className="error-actions">
            <button type="button" className="btn-secondary" onClick={onRetry}>
              Retry
            </button>
            <button type="button" className="btn-secondary" onClick={onEditQuery}>
              Edit query
            </button>
          </div>
        </div>
      ) : (
        <div className="live-panels">
          <div className="live-main">
            <div className="paper-counter">papers found: {state.paperCount}</div>
            <StructuredQuery
              framework={state.framework}
              picot={state.picot}
              spice={state.spice}
              detectedDomains={state.detectedDomains}
              complexity={state.complexity}
              rewrittenQuery={state.rewrittenQuery}
              compact
            />
            <SpecialistPanels
              specialists={state.specialistText}
              phase3Status={state.phaseStatus[2]}
              pendingDomains={state.detectedDomains}
              activeTool={state.activeTool}
            />
            {showSynthesis ? (
              <div className="synthesis-preview">
                <p className="section-label">Synthesis</p>
                {state.supervisorAnswer ? (
                  <p
                    className={`spec-text${
                      synthesizePhase === "active" ? " streaming" : ""
                    }`}
                  >
                    {state.supervisorAnswer}
                  </p>
                ) : (
                  <div className="synthesis-waiting">
                    <div className="sk-line w92" />
                    <div className="sk-line w78" />
                    <p className="live-status-detail">Assembling final recommendation…</p>
                  </div>
                )}
              </div>
            ) : null}
          </div>
          <ActivityLog
            rows={state.log}
            eventCount={state.logEventCount}
            connecting={state.runStatus === "connecting"}
          />
        </div>
      )}
    </main>
  );
}
